import { useEffect } from "react";

interface StructuredDataProps {
  type: "LocalBusiness" | "Service" | "Review" | "ServiceArea";
  data: any;
}

export default function StructuredData({ type, data }: StructuredDataProps) {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(data);
    script.id = `structured-data-${type.toLowerCase()}`;
    
    // Remove existing script if present
    const existing = document.getElementById(script.id);
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(script.id);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, data]);
  
  return null;
}

// Helper function to generate LocalBusiness schema
export function getLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "HousePainter",
    "name": "Danis Painting Co.",
    "image": "https://danispainting.net/images/logo.png",
    "logo": "https://danispainting.net/images/logo.png",
    "@id": "https://danispainting.net",
    "url": "https://danispainting.net",
    "telephone": "+1-805-403-8727",
    "email": "danispainting@gmail.com",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "133 E De La Guerra St, #267",
      "addressLocality": "Santa Barbara",
      "addressRegion": "CA",
      "postalCode": "93101",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 34.4208,
      "longitude": -119.6982
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "07:00",
      "closes": "18:00"
    },
    "hasMap": "https://www.google.com/maps/place/Danis+Painting+Co./@34.4217374,-119.6976324,17z/data=!3m1!4b1!4m6!3m5!1s0x80e9151e40117129:0xc31253e4eab1f0c9",
    "sameAs": [
      "https://www.google.com/maps/place/Danis+Painting+Co./@34.4217374,-119.6976324,17z/data=!3m1!4b1!4m6!3m5!1s0x80e9151e40117129:0xc31253e4eab1f0c9",
      "https://www.facebook.com/p/Mark-Danis-Painting-Co-100051124372469/",
      "https://www.instagram.com/danispaintingco/",
      "https://www.yelp.com/biz/danis-painting-santa-barbara-4",
      "https://www.bbb.org/us/ca/santa-barbara/profile/painting-contractors/danis-painting-1236-92008136",
      "https://www.angi.com/companylist/us/ca/santa-barbara/danis-painting-reviews-6175837.htm",
      "https://www.homeadvisor.com/rated.DANISPAINTINGCO.104191503.html"
    ],
    "knowsAbout": [
      "Interior Painting",
      "Exterior Painting",
      "Commercial Painting",
      "Residential Painting",
      "Cabinet Painting",
      "Roof Coating",
      "Waterproofing",
      "Pressure Washing",
      "Drywall Repair",
      "Deck Repair",
      "Trim and Molding"
    ],
    "slogan": "Honest New England Quality",
    "foundingDate": "1994",
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "value": 10
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Santa Barbara",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Ventura",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Montecito",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Goleta",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Carpinteria",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Ojai",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Camarillo",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Solvang",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Buellton",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Santa Ynez",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Isla Vista",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Summerland",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Oxnard",
        "containedIn": {
          "@type": "State",
          "name": "California"
        }
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "bestRating": "5",
      "worstRating": "1",
      "reviewCount": "24"
    },
    "review": [
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Susan K."
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5",
          "worstRating": "1"
        },
        "reviewBody": "Outstanding quality and craftsmanship! They did extensive drywall repair and painting in our home. The results were flawless. True professionals in every sense.",
        "datePublished": "2024-08-20"
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Robert H."
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5",
          "worstRating": "1"
        },
        "reviewBody": "Danis Painting exceeded our expectations on our estate's exterior refresh. Their attention to detail and respect for our property was exceptional. Highly recommend!",
        "datePublished": "2024-09-15"
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Maria L."
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5",
          "worstRating": "1"
        },
        "reviewBody": "Mark and his crew painted our entire home interior and exterior. Professional, clean, and the results are beautiful. They worked around our schedule and finished on time.",
        "datePublished": "2024-10-22"
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "James T."
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5",
          "worstRating": "1"
        },
        "reviewBody": "Used Danis Painting for our commercial building exterior. They completed the job efficiently with minimal disruption to our tenants. Great communication throughout the project.",
        "datePublished": "2024-11-08"
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Patricia W."
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "4",
          "bestRating": "5",
          "worstRating": "1"
        },
        "reviewBody": "Good quality work on our deck staining and waterproofing. The team was professional and the deck looks great. Would use them again for future projects.",
        "datePublished": "2024-12-01"
      }
    ],
    "hasCredential": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "license",
      "name": "California Contractor License",
      "identifier": "662554",
      "recognizedBy": {
        "@type": "Organization",
        "name": "California Contractors State License Board"
      }
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "License Number",
        "value": "LIC #662554"
      },
      {
        "@type": "PropertyValue",
        "name": "Issuing Authority",
        "value": "California Contractors State License Board (CSLB)"
      }
    ]
  };
}

// City geo coordinates for Service Area schema
const cityGeoData: Record<string, { lat: number; lng: number; county: string }> = {
  "santa-barbara": { lat: 34.4208, lng: -119.6982, county: "Santa Barbara County" },
  "ventura": { lat: 34.2746, lng: -119.2290, county: "Ventura County" },
  "montecito": { lat: 34.4367, lng: -119.6318, county: "Santa Barbara County" },
  "goleta": { lat: 34.4358, lng: -119.8276, county: "Santa Barbara County" },
  "carpinteria": { lat: 34.3989, lng: -119.5184, county: "Santa Barbara County" },
  "ojai": { lat: 34.4480, lng: -119.2429, county: "Ventura County" },
  "camarillo": { lat: 34.2164, lng: -119.0376, county: "Ventura County" },
  "solvang": { lat: 34.5958, lng: -120.1376, county: "Santa Barbara County" },
  "buellton": { lat: 34.6133, lng: -120.1926, county: "Santa Barbara County" },
  "santa-ynez": { lat: 34.6128, lng: -120.0798, county: "Santa Barbara County" },
  "isla-vista": { lat: 34.4133, lng: -119.8610, county: "Santa Barbara County" },
  "summerland": { lat: 34.4214, lng: -119.5965, county: "Santa Barbara County" },
  "oxnard": { lat: 34.1975, lng: -119.1771, county: "Ventura County" },
  "los-angeles": { lat: 34.0522, lng: -118.2437, county: "Los Angeles County" },
};

// Helper function to generate city-specific LocalBusiness schema with Service Area
export function getLocalBusinessSchemaForCity(citySlug: string, cityName: string, pageUrl: string) {
  const geo = cityGeoData[citySlug] || cityGeoData["santa-barbara"];
  const baseSchema = getLocalBusinessSchema();
  const googleMapsUrl = `https://www.google.com/maps/search/Danis+Painting+Co+${encodeURIComponent(cityName)}+CA`;
  
  return {
    ...baseSchema,
    "@id": `https://danispainting.net/city/${citySlug}#localbusiness`,
    "url": pageUrl,
    "description": `Professional painting contractor serving ${cityName}, CA. Danis Painting Co. offers commercial painting, residential painting, cabinet painting, waterproofing, roof coating, and drywall repair in ${cityName} and surrounding areas. Over 30 years of experience. Licensed & Insured. Free estimates.`,
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": geo.lat,
      "longitude": geo.lng
    },
    "hasMap": googleMapsUrl,
    "areaServed": {
      "@type": "City",
      "name": cityName,
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": geo.county
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": geo.lat,
        "longitude": geo.lng
      }
    },
    "makesOffer": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": `Commercial Painting in ${cityName}`,
          "url": `https://danispainting.net/service/commercial-painting/${citySlug}`,
          "description": `Professional commercial painting services for businesses in ${cityName}, CA.`
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": `Residential Painting in ${cityName}`,
          "url": `https://danispainting.net/service/interior-exterior-painting/${citySlug}`,
          "description": `Expert interior and exterior residential painting in ${cityName}, CA.`
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": `Cabinet Painting in ${cityName}`,
          "url": `https://danispainting.net/service/cabinet-painting/${citySlug}`,
          "description": `Professional cabinet painting and refinishing in ${cityName}, CA.`
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": `Waterproofing in ${cityName}`,
          "url": `https://danispainting.net/service/waterproofing/${citySlug}`,
          "description": `Professional waterproofing services for decks, balconies, and exterior surfaces in ${cityName}, CA.`
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": `Roof Coating in ${cityName}`,
          "url": `https://danispainting.net/service/roof-coating-repair/${citySlug}`,
          "description": `GE Enduris roof coating and repair services in ${cityName}, CA.`
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": `Drywall Repair in ${cityName}`,
          "url": `https://danispainting.net/service/drywall-repair/${citySlug}`,
          "description": `Expert drywall patching, repair, and finishing in ${cityName}, CA.`
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": `Pressure Washing in ${cityName}`,
          "url": `https://danispainting.net/service/pressure-washing/${citySlug}`,
          "description": `Professional pressure washing and surface cleaning in ${cityName}, CA.`
        }
      }
    ]
  };
}

// Helper function to generate location-specific Service Area schema
export function getServiceAreaSchema(citySlug: string, cityName: string) {
  const geo = cityGeoData[citySlug] || cityGeoData["santa-barbara"];
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Painting Contractor",
    "provider": {
      "@type": "ProfessionalService",
      "name": "Danis Painting Co.",
      "telephone": "+1-805-403-8727",
      "url": "https://danispainting.net"
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": geo.lat,
        "longitude": geo.lng
      },
      "geoRadius": "15000"
    },
    "serviceArea": {
      "@type": "City",
      "name": cityName,
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": geo.county
      }
    },
    "description": `Professional painting, waterproofing, and property maintenance services in ${cityName}, CA. Over 30 years of quality craftsmanship.`,
    "url": `https://danispainting.net/city/${citySlug}`
  };
}

// Helper function to generate service-location-specific schema with GeoCoordinates
// Used on all 110 service+location combination pages
export function getServiceLocationSchema(
  serviceSlug: string,
  serviceName: string,
  serviceDescription: string,
  citySlug: string,
  cityName: string
) {
  const geo = cityGeoData[citySlug] || cityGeoData["santa-barbara"];
  const pageUrl = `https://danispainting.net/service/${serviceSlug}/${citySlug}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${pageUrl}#service`,
    "serviceType": serviceName,
    "name": `${serviceName} Services in ${cityName}`,
    "description": `Professional ${serviceName.toLowerCase()} services in ${cityName}, CA. ${serviceDescription}`,
    "url": pageUrl,
    "provider": {
      "@type": "HousePainter",
      "name": "Danis Painting Co.",
      "telephone": "+1-805-403-8727",
      "email": "danispainting@gmail.com",
      "url": "https://danispainting.net",
      "priceRange": "$$",
      "image": "https://danispainting.net/images/logo.png",
      "logo": "https://danispainting.net/images/logo.png",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "133 E De La Guerra St, #267",
        "addressLocality": "Santa Barbara",
        "addressRegion": "CA",
        "postalCode": "93101",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": geo.lat,
        "longitude": geo.lng
      },
      "hasCredential": {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "license",
        "name": "California Contractor License",
        "identifier": "662554",
        "recognizedBy": {
          "@type": "Organization",
          "name": "California Contractors State License Board"
        }
      },
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "License Number",
          "value": "LIC #662554"
        },
        {
          "@type": "PropertyValue",
          "name": "Issuing Authority",
          "value": "California Contractors State License Board (CSLB)"
        }
      ]
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": geo.lat,
        "longitude": geo.lng
      },
      "geoRadius": "15000"
    },
    "serviceArea": {
      "@type": "City",
      "name": cityName,
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": geo.county
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": geo.lat,
        "longitude": geo.lng
      }
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "USD",
      "areaServed": {
        "@type": "City",
        "name": cityName
      }
    }
  };
}

// Helper function to generate Service schema
export function getServiceSchema(serviceName: string, description: string, url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceName,
    "provider": {
      "@type": "ProfessionalService",
      "name": "Danis Painting Co.",
      "telephone": "+1-805-403-8727",
      "email": "danispainting@gmail.com",
      "url": "https://danispainting.net",
      "priceRange": "$$",
      "image": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/gallery-5_d36da2e9_a4c69cb6.webp",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "133 E De La Guerra St, #267",
        "addressLocality": "Santa Barbara",
        "addressRegion": "CA",
        "postalCode": "93101",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "State",
      "name": "California"
    },
    "description": description,
    "url": url,
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "USD"
    }
  };
}
